/**
 * Contenido de los artículos del blog. Cada slug es una URL pública
 * (ej. /blog/como-crear-servidor-minecraft) que se pre-renderiza en build.
 *
 * Mantener el contenido aquí (en vez de MDX) porque:
 *  - Es TypeScript puro, autocompletado y refactor seguro
 *  - Se puede importar en otros sitios (cards del hub, related links)
 *  - Compatible con el prerender script sin plugins extra
 */

export interface ArticleSection {
  type: 'paragraph' | 'heading' | 'list' | 'code' | 'callout' | 'video'
  /** Para paragraph/heading/callout: texto. Para list: array. Para code: string multilínea. */
  content: string | string[]
  /** Solo para heading: nivel 2 o 3 */
  level?: 2 | 3
  /** Solo para callout: tipo */
  variant?: 'tip' | 'warn' | 'info'
}

export interface Article {
  slug: string
  title: string
  description: string
  category: 'Guía' | 'Comparativa' | 'Tutorial'
  date: string // ISO
  readMinutes: number
  author: string
  /** Bloques de contenido en orden */
  body: ArticleSection[]
}

export const ARTICLES: Article[] = [
  {
    slug: 'como-crear-servidor-minecraft',
    title: 'Cómo crear un servidor de Minecraft en Voltary (guía paso a paso)',
    description:
      'Tutorial completo: crea tu servidor de Minecraft en Voltary en menos de 5 minutos.',
    category: 'Tutorial',
    date: '2026-08-14',
    readMinutes: 6,
    author: 'Equipo Voltary',
    body: [
      {
        type: 'paragraph',
        content:
          'Crear un servidor de Minecraft en Voltary toma menos de 5 minutos. Esta guía te lleva paso a paso.',
      },

      { type: 'heading', level: 2, content: 'Antes de empezar' },
      {
        type: 'list',
        content: [
          'Una cuenta de Discord (es lo que usamos para autenticarte).',
          'Saldo en tu cuenta de Voltary para encender el servidor. Recargas desde $1 USD.',
          'El cliente de Minecraft Java Edition en tu PC.',
        ],
      },

      { type: 'heading', level: 2, content: 'Crea tu cuenta en Voltary' },
      {
        type: 'list',
        content: [
          'Ve a voltary.org y haz click en "Iniciar sesión".',
          'Selecciona "Continuar con Discord" y autoriza la aplicación.',
          'Tu cuenta queda creada automáticamente con tu ID de Discord. Sin contraseñas adicionales.',
        ],
      },

      { type: 'heading', level: 2, content: 'Recarga saldo' },
      {
        type: 'paragraph',
        content:
          'El servidor cobra por horas, así que necesitas saldo en tu cuenta antes de encenderlo. Para empezar a probar, una recarga de $1-5 USD es suficiente (te alcanza para muchas horas de juego).',
      },
      {
        type: 'list',
        content: [
          'Ve a "Recargar" en el menú principal.',
          'Elige el monto (mínimo $1 USD).',
          'Selecciona el método de pago: Zinli, Visa, Kontigo, Binance o PayPal.',
          'Confirma el pago. El saldo aparece en segundos.',
        ],
      },

      { type: 'heading', level: 2, content: 'Crea el servidor' },
      {
        type: 'list',
        content: [
          'En el panel principal, haz click en "Create Server".',
          'Juego: selecciona "Minecraft".',
          'Tipo de servidor: elige Vanilla, Spigot, Paper, Fabric o Forge según lo que quieras correr.',
          'Versión: elige la oficial que quieras (cualquier versión oficial desde 1.8 en adelante).',
          'Plan: elige según la RAM que necesites. Para 3-5 amigos en vanilla, el Plan Básico (4 GB) basta.',
          'Nombre: ponle un nombre al servidor (lo verás tú, no se muestra a los jugadores).',
          'Haz click en "Crear". El servidor empieza a aprovisionarse.',
        ],
      },
      {
        type: 'paragraph',
        content:
          'En menos de 2 minutos el servidor está encendido y listo para recibir jugadores. Verás un panel con el estado en tiempo real, la IP/subdominio, y los controles de administración.',
      },

      { type: 'heading', level: 2, content: 'Comparte la IP con tus amigos' },
      {
        type: 'paragraph',
        content:
          'En el panel del servidor verás la IP pública (algo como tu-servidor.node.voltary.org) y el puerto. Comparte esa IP con tus amigos por Discord, WhatsApp o donde prefieras.',
      },
      {
        type: 'paragraph',
        content:
          'Tus amigos la añaden en Minecraft → Multijugador → Añadir servidor. Una vez conectados, pueden entrar.',
      },

      { type: 'heading', level: 2, content: 'Cuándo detienes la sección' },
      {
        type: 'paragraph',
        content:
          'El servidor cobra por hora activa. Cuando termines de jugar, debes detener la sesión desde el panel: el cobro se detiene en ese momento. Tu mundo, tu configuración y tu whitelist se guardan para la próxima sesión.',
      },
      {
        type: 'callout',
        variant: 'tip',
        content:
          'El almacenamiento sigue activo aunque el servidor esté apagado: ~$0.015/GB/mes para los GB extra que uses. En el Plan Básico tienes 40 GB incluidos, suficiente para mundos grandes.',
      },
      {
        type: 'paragraph',
        content:
          '¿Dudas? El equipo de Voltary responde en Discord directo desde el panel. Sin tickets ni esperas.',
      },
      {
        type: 'heading',
        level: 2,
        content: 'Video tutorial',
      },
      {
        type: 'paragraph',
        content:
          'Si prefieres ver el proceso en video, aquí tienes el tutorial completo:',
      },
      {
        type: 'video',
        content: '/video/tutorial.mp4',
      },
    ],
  },
]

export function getArticleBySlug(slug: string): Article | undefined {
  return ARTICLES.find((a) => a.slug === slug)
}